import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PasswordField } from './PasswordField';
import { passwordValidation } from '../schemas/validation';

const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: passwordValidation,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}).refine((data) => data.currentPassword !== data.newPassword, {
  message: "New password must be different from current password",
  path: ["newPassword"],
});

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

interface ChangePasswordFormProps {
  onSubmit: (data: ChangePasswordFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [error, setError] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const handleFormSubmit = async (data: ChangePasswordFormData) => {
    try {
      setError('');
      await onSubmit(data);
      reset();
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="auth-form">
      <h3>Change Password</h3>
      
      {error && <div className="error-message">{error}</div>}
      
      <PasswordField
        id="currentPassword"
        label="Current Password"
        placeholder="Enter your current password"
        register={register}
        error={errors.currentPassword?.message}
        disabled={loading}
      />

      <PasswordField
        id="newPassword"
        label="New Password"
        placeholder="Enter your new password"
        register={register}
        error={errors.newPassword?.message}
        disabled={loading}
        showRequirements={true}
      />

      <PasswordField
        id="confirmPassword"
        label="Confirm New Password"
        placeholder="Confirm your new password"
        register={register}
        error={errors.confirmPassword?.message}
        disabled={loading}
      />

      <div className="form-actions">
        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary"
        >
          {loading ? 'Changing Password...' : 'Change Password'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="btn btn-secondary"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};